import { BranchGapRequirement, CareerGapAnalysis, CareerLevel, ConsultantNode } from '../types/career';
import { getLevelDefinition } from './careerRules';

/**
 * Checks if a list of direct branch levels satisfies the required branch pattern.
 * Uses greedy descending vector comparison (O(K log K)).
 *
 * Example:
 * directLevels = [5, 4, 3, 2]
 * requiredPattern = [4, 4, 3, 2]
 * Sorted direct: [5, 4, 3, 2]
 * Sorted req:    [4, 4, 3, 2]
 * Comparison: 5>=4, 4>=4, 3>=3, 2>=2 -> true
 */
export function checkBranchQualification(directLevels: number[], requiredPattern: number[]): boolean {
  if (!requiredPattern || requiredPattern.length === 0) {
    return true;
  }
  if (!directLevels || directLevels.length < requiredPattern.length) {
    return false;
  }

  const sortedDirect = [...directLevels].sort((a, b) => b - a);
  const sortedReq = [...requiredPattern].sort((a, b) => b - a);

  for (let i = 0; i < sortedReq.length; i++) {
    if (sortedDirect[i] < sortedReq[i]) {
      return false;
    }
  }

  return true;
}

/**
 * Checks if a consultant meets all criteria to qualify for a specific target career level.
 */
export function canQualifyForLevel(
  targetLevel: CareerLevel,
  cumTotalAE: number,
  cumPersonalAE: number,
  directBranchLevels: number[]
): boolean {
  const def = getLevelDefinition(targetLevel);

  // 1. Check cumulative total units threshold
  if (cumTotalAE < def.minCumulativeUnits) {
    return false;
  }

  // 2. Check mandatory personal units (e.g. Level 3 requires min 300 personal AE)
  const minPersonal = def.minPersonalUnits ?? 0;
  if (cumPersonalAE < minPersonal) {
    return false;
  }

  // 3. Check direct branch configuration
  const branchReq = def.requiredBranches ?? [];
  if (!checkBranchQualification(directBranchLevels, branchReq)) {
    return false;
  }

  return true;
}

/**
 * Evaluates the highest eligible career level for a consultant.
 * Respects the Ratchet Principle (level never decreases for established leaders with required volume).
 * Iterates forward from Level 1 up to 8, strictly enforcing that a consultant can NEVER hold a career level
 * unless their cumulative total AE and personal AE satisfy the mandatory minimum thresholds for that level.
 */
export function evaluateCareerLevel(
  currentLevel: CareerLevel,
  cumTotalAE: number,
  cumPersonalAE: number,
  directBranchLevels: number[]
): CareerLevel {
  let highestLevel = currentLevel;

  for (let target = (highestLevel + 1) as CareerLevel; target <= 8; target++) {
    if (canQualifyForLevel(target as CareerLevel, cumTotalAE, cumPersonalAE, directBranchLevels)) {
      highestLevel = target as CareerLevel;
    } else {
      // Must qualify sequentially
      break;
    }
  }

  return highestLevel;
}

/**
 * Performs a gap analysis for reaching the next career level (or target level).
 * Returns missing units, missing personal AE, branch gaps, and overall readiness.
 */
export function calculateCareerGap(
  currentLevel: CareerLevel,
  cumTotalAE: number,
  cumPersonalAE: number,
  directBranchLevels: number[],
  explicitTargetLevel?: CareerLevel
): CareerGapAnalysis {
  const targetLevel = explicitTargetLevel ?? (currentLevel < 8 ? ((currentLevel + 1) as CareerLevel) : null);

  if (!targetLevel) {
    // Already at top level (Level 8)
    const currentDef = getLevelDefinition(8);
    return {
      currentLevel: 8,
      nextLevel: null,
      targetThresholdAE: currentDef.minCumulativeUnits,
      missingUnitsTotalAE: 0,
      currentUnitsTotalAE: cumTotalAE,
      progressPercent: 100,
      missingPersonalAE: 0,
      isPersonalConditionMet: true,
      branchRequirements: [],
      isBranchConditionMet: true,
      isFullyQualified: true,
    };
  }

  const targetDef = getLevelDefinition(targetLevel);

  // Total units gap
  const missingUnitsTotalAE = Math.max(0, targetDef.minCumulativeUnits - cumTotalAE);
  const progressPercent =
    targetDef.minCumulativeUnits > 0
      ? Math.min(100, Math.round((cumTotalAE / targetDef.minCumulativeUnits) * 100))
      : 100;

  // Personal units gap
  const minPersonal = targetDef.minPersonalUnits ?? 0;
  const missingPersonalAE = Math.max(0, minPersonal - cumPersonalAE);
  const isPersonalConditionMet = missingPersonalAE === 0;

  // Branch gaps analysis
  const reqBranches = targetDef.requiredBranches ?? [];
  const branchRequirements: BranchGapRequirement[] = [];

  if (reqBranches.length > 0) {
    // Group required branches by level, e.g. [4, 4, 3, 2] -> {4: 2, 3: 1, 2: 1}
    const reqCountsByLevel = new Map<CareerLevel, number>();
    for (const lvl of reqBranches) {
      const c = reqCountsByLevel.get(lvl as CareerLevel) || 0;
      reqCountsByLevel.set(lvl as CareerLevel, c + 1);
    }

    // Sort descending by level
    const sortedLevels = Array.from(reqCountsByLevel.keys()).sort((a, b) => b - a);

    // Calculate cumulative requirements and availability
    // For Level 4 in [4,4,3,2]: need 2 branches >= 4
    // For Level 3 in [4,4,3,2]: need 2 + 1 = 3 branches >= 3
    // For Level 2 in [4,4,3,2]: need 2 + 1 + 1 = 4 branches >= 2
    let cumulativeNeeded = 0;
    for (const lvl of sortedLevels) {
      const countForThisLevel = reqCountsByLevel.get(lvl)!;
      cumulativeNeeded += countForThisLevel;

      const currentAtOrAbove = directBranchLevels.filter((d) => d >= lvl).length;
      const missingForThreshold = Math.max(0, cumulativeNeeded - currentAtOrAbove);

      branchRequirements.push({
        requiredLevel: lvl,
        count: countForThisLevel,
        currentQualifiedCount: directBranchLevels.filter((d) => d === lvl).length,
        missingCount: missingForThreshold,
      });
    }
  }

  const isBranchConditionMet = checkBranchQualification(directBranchLevels, reqBranches);
  const isFullyQualified = missingUnitsTotalAE === 0 && isPersonalConditionMet && isBranchConditionMet;

  return {
    currentLevel,
    nextLevel: targetLevel,
    targetThresholdAE: targetDef.minCumulativeUnits,
    missingUnitsTotalAE,
    currentUnitsTotalAE: cumTotalAE,
    progressPercent,
    missingPersonalAE,
    isPersonalConditionMet,
    branchRequirements,
    isBranchConditionMet,
    isFullyQualified,
  };
}

/**
 * Bottom-up post-order evaluation of all career levels across an entire organization tree.
 * Returns a cloned tree with recalculated cumulative production and promotions.
 */
export function evaluateTreeQualifications(node: ConsultantNode): ConsultantNode {
  // 1. Post-order: First evaluate all children
  const updatedChildren = node.children.map((child) => evaluateTreeQualifications(child));

  // 2. Aggregate structural turnover from children
  const childrenMonthlyTurnover = updatedChildren.reduce((sum, c) => sum + (c.totalMonthlyAE ?? 0), 0);
  const childrenCumulativeTurnover = updatedChildren.reduce((sum, c) => sum + (c.totalCumulativeAE ?? 0), 0);

  // When baselineTeamCumulativeAE is specified, team cumulative units is baseline + all children cumulative units.
  // Otherwise, default to the greater of existing teamCumulativeAE and childrenCumulativeTurnover.
  const effectiveTeamCumulative =
    node.baselineTeamCumulativeAE !== undefined
      ? node.baselineTeamCumulativeAE + childrenCumulativeTurnover
      : Math.max(node.teamCumulativeAE ?? 0, childrenCumulativeTurnover);

  const totalMonthlyAE = node.personalMonthlyAE + childrenMonthlyTurnover;
  const totalCumulativeAE = node.personalCumulativeAE + effectiveTeamCumulative;

  // 3. Direct branch levels
  const directBranchLevels = updatedChildren.map((c) => c.level);

  // 4. Evaluate new career level
  const newLevel = evaluateCareerLevel(
    node.level,
    totalCumulativeAE,
    node.personalCumulativeAE,
    directBranchLevels
  );

  return {
    ...node,
    level: newLevel,
    teamMonthlyAE: childrenMonthlyTurnover,
    teamCumulativeAE: effectiveTeamCumulative,
    totalMonthlyAE,
    totalCumulativeAE,
    children: updatedChildren,
  };
}
